import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Form,
  message,
  Modal,
  Space,
  Spin,
  Steps,
  Typography,
} from 'antd';
import { useForm } from 'antd/es/form/Form';
import type { ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { QUERY_KEYS } from '../../Constants/queryKeys';
import {
  ArchiveAnnualCollaborationDocuments,
  GetUniversities,
  GetUniversitySetup,
  StartStudyYearWithSetup,
} from '../../helper/apiHelper';
import type { NewStudyYear, University } from '../../Types/interfaces';
import { formatDate } from '../../Utils/date';
import { AcademicCalendarComponent } from '../AcademicCalendar/AcademicCalendarComponent';
import { DepartmentFormComponent } from './DepartmentFormComponent';
import { InternshipSessionFormComponent } from './InternshipSessionFormComponent';
import { StudyLineFormComponent } from './StudyLineFormComponent';
import {
  buildDepartmentsSetup,
  toSetupFormValues,
} from './universitySetupUtils';

// The form fields of the steps before the calendar, they are checked before going to the next step
const STEP_FIELDS = ['departments', 'studyLines', 'sessions'];
// The start / end of the study year is chosen before the semesters of the calendar
const STUDY_YEAR_STEP = STEP_FIELDS.length;
const CALENDAR_STEP = STUDY_YEAR_STEP + 1;

// The rector sets up a new study year step by step: departments, study lines, sessions,
// the start / end of the study year and the calendar, and they are saved together,
// so a study year is never started without its setup
export const StudyYearSetupComponent = (): ReactNode => {
  const { t } = useTranslation();
  const [form] = useForm();
  const queryClient = useQueryClient();
  const [messageApi, messageContextHolder] = message.useMessage();
  const [modal, modalContextHolder] = Modal.useModal();
  const [currentStep, setCurrentStep] = useState(0);
  const departmentNames = useRef<Array<string>>([]);
  const studyLineNames = useRef<Array<Array<string>>>([]);
  // The calendar of the new study year, undefined if the selected study year is already created
  const [studyYear, setStudyYear] = useState<NewStudyYear>();
  // false while a semester of the calendar doesn't fit into the study year
  const [isCalendarValid, setIsCalendarValid] = useState(true);

  const { data: universities, isLoading } = useQuery({
    queryKey: [...QUERY_KEYS.UNIVERSITIES],
    queryFn: GetUniversities,
  });
  const university = universities?.[0];

  // The setup of the current study year, the new study year starts from it
  const { data: previousSetup, isLoading: isLoadingPreviousSetup } = useQuery({
    queryKey: [...QUERY_KEYS.UNIVERSITY_SETUP, university?.id],
    queryFn: () => GetUniversitySetup(university?.id ?? 0),
    enabled: university !== undefined,
  });

  // The forms are filled with the previous setup: without changes only Next has to be clicked,
  // and anything can be changed before the new study year is started
  useEffect(() => {
    if (!previousSetup) {
      return;
    }
    const {
      values,
      departmentNames: names,
      studyLineNames: lineNames,
    } = toSetupFormValues(previousSetup);
    departmentNames.current = names;
    studyLineNames.current = lineNames;
    form.resetFields();
    form.setFieldsValue(values);
  }, [form, previousSetup]);

  const startStudyYearMutation = useMutation({
    mutationFn: async ({
      universityId,
      newStudyYear,
      values,
    }: {
      universityId: number;
      newStudyYear: NewStudyYear;
      values: University;
    }) => {
      const savedStudyYear = await StartStudyYearWithSetup(universityId, {
        studyYear: newStudyYear,
        setup: { departmentsSetup: buildDepartmentsSetup(values) },
      });
      await ArchiveAnnualCollaborationDocuments();
      return savedStudyYear;
    },
    onSuccess: async (savedStudyYear) => {
      // The saved study year is the new current one, so it doesn't need to be loaded again
      queryClient.setQueryData(QUERY_KEYS.CURRENT_STUDY_YEAR, savedStudyYear);
      await queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.STUDY_YEARS,
      });
      // the saved setup is the previous one for the next study year, the forms are filled with it again
      await queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.UNIVERSITY_SETUP,
      });
      messageApi.success(t('studyYearSetup.success'));
      setCurrentStep(0);
    },
    onError: (error) => {
      messageApi.error(
        error instanceof Error && error.message
          ? error.message
          : t('studyYearSetup.error')
      );
    },
  });

  // Only the selected department / study line is shown in the study line and session steps,
  // so the fields of the others aren't checked by the form: these checks use the values of all of them
  const findMissingSetupData = (step: number): string | undefined => {
    const values = form.getFieldsValue(true) as Partial<University>;
    const departments = values.departments ?? [];
    const studyLines = values.studyLines ?? [];
    const isFilled = (value: unknown): boolean =>
      value !== undefined && value !== null && String(value).trim() !== '';
    const list = (names: Array<string | undefined>): string =>
      names.map((name) => name || '-').join(', ');

    if (STEP_FIELDS[step] === 'studyLines') {
      const withoutStudyLines = departments.filter(
        (_, index) => (studyLines[index] ?? []).length === 0
      );
      if (withoutStudyLines.length > 0) {
        return t('studyYearSetup.errors.missingStudyLines', {
          names: list(withoutStudyLines.map((d) => d?.departmentName)),
        });
      }
      const incomplete = departments.filter((_, index) =>
        (studyLines[index] ?? []).some(
          (line) =>
            !isFilled(line?.studyLineName) ||
            !isFilled(line?.studyLineLevel) ||
            !isFilled(line?.studyLineType) ||
            !isFilled(line?.teachingLanguage) ||
            // 0 comes from a study line saved before the number of years existed
            !line?.numberOfYears
        )
      );
      if (incomplete.length > 0) {
        return t('studyYearSetup.errors.incompleteStudyLines', {
          names: list(incomplete.map((d) => d?.departmentName)),
        });
      }
      const withoutCoordinators = studyLines
        .flat()
        .filter((line) => !(line?.coordinators ?? []).some(isFilled));
      if (withoutCoordinators.length > 0) {
        return t('studyYearSetup.errors.missingCoordinators', {
          names: list(withoutCoordinators.map((line) => line?.studyLineName)),
        });
      }
    }

    if (STEP_FIELDS[step] === 'sessions') {
      // the sessions are listed one after the other for the study lines of all departments
      const lineNames = studyLines.flat().map((line) => line?.studyLineName);
      const sessions = values.sessions ?? [];
      const withoutSessions = lineNames.filter(
        (_, index) => (sessions[index] ?? []).length === 0
      );
      if (withoutSessions.length > 0) {
        return t('studyYearSetup.errors.missingSessions', {
          names: list(withoutSessions),
        });
      }
      const incomplete = lineNames.filter((_, index) =>
        (sessions[index] ?? []).some(
          (session) =>
            !isFilled(session?.description) ||
            !isFilled(session?.yearsOfStudy) ||
            !isFilled(session?.courseCredit) ||
            !isFilled(session?.sessionHours)
        )
      );
      if (incomplete.length > 0) {
        return t('studyYearSetup.errors.incompleteSessions', {
          names: list(incomplete),
        });
      }
    }

    return undefined;
  };

  const goToNextStep = async (): Promise<void> => {
    // The start / end of the study year is always valid, the calendar doesn't let it be set otherwise
    if (currentStep >= STEP_FIELDS.length) {
      setCurrentStep(currentStep + 1);
      return;
    }
    try {
      // Only the fields of the current step are checked, the next steps are still empty
      await form.validateFields([STEP_FIELDS[currentStep]], {
        recursive: true,
      });
      const missing = findMissingSetupData(currentStep);
      if (missing) {
        messageApi.error(missing, 8);
        return;
      }
      setCurrentStep(currentStep + 1);
    } catch {
      // the errors are shown at the fields
    }
  };

  const submit = (): void => {
    // a semester which doesn't fit into the study year is shown at the calendar
    if (!university || !studyYear || !isCalendarValid) {
      return;
    }

    modal.confirm({
      title: t('studyYear.newStudyYear'),
      content: t('academicCalendar.newStudyYearConfirm', {
        start: formatDate(studyYear.startDate),
        end: formatDate(studyYear.endDate),
      }),
      centered: true,
      okText: t('academicCalendar.newStudyYearOk'),
      cancelText: t('academicCalendar.cancel'),
      // The modal stays open with a loading OK button until the request finishes;
      // errors are already shown by onError, so the modal just closes
      onOk: () =>
        startStudyYearMutation
          .mutateAsync({
            universityId: university.id,
            newStudyYear: studyYear,
            values: form.getFieldsValue(true) as University,
          })
          .catch(() => undefined),
    });
  };

  if (isLoading || isLoadingPreviousSetup) {
    return <Spin />;
  }

  if (!university) {
    return (
      <Alert
        type="warning"
        showIcon
        message={t('studyYearSetup.noUniversity')}
      />
    );
  }

  // Every step stays rendered, only the current one is shown, so the values are kept when going back
  const steps = [
    {
      id: 'departments',
      intro: t('studyYearSetup.intro.departments'),
      title: t('uniForm.departmentsLabel'),
      content: (
        <DepartmentFormComponent
          departmentNames={departmentNames}
          studyLineNames={studyLineNames}
        />
      ),
    },
    {
      id: 'studyLines',
      intro: t('studyYearSetup.intro.studyLines'),
      title: t('uniForm.studyLinesLabel'),
      content: (
        <StudyLineFormComponent
          departmentNames={departmentNames}
          studyLineNames={studyLineNames}
        />
      ),
    },
    {
      id: 'sessions',
      intro: t('studyYearSetup.intro.sessions'),
      title: t('uniForm.sessionsLabel'),
      content: (
        <InternshipSessionFormComponent
          departmentNames={departmentNames}
          studyLineNames={studyLineNames}
        />
      ),
    },
    // These two steps show the same calendar below, so it keeps its changes between them
    {
      id: 'studyYear',
      intro: t('studyYearSetup.intro.studyYear'),
      title: t('academicCalendar.studyYear'),
      content: null,
    },
    {
      id: 'calendar',
      intro: t('studyYearSetup.intro.calendar'),
      title: t('homePage.academicCalendar'),
      content: null,
    },
  ];

  return (
    <Form form={form} layout="vertical">
      {messageContextHolder}
      {modalContextHolder}

      <Steps
        current={currentStep}
        items={steps.map(({ title }) => ({ title }))}
        style={{ marginBottom: 24 }}
      />

      {/* stable keys: a translated title as key would rebuild the steps (and lose the form lists) on a language change */}
      {steps.map(({ id, intro, content }, index) => (
        <div
          key={id}
          style={{ display: index === currentStep ? 'block' : 'none' }}
        >
          <Typography.Paragraph type="secondary" style={{ marginBottom: 20 }}>
            {intro}
          </Typography.Paragraph>
          {content}
        </div>
      ))}

      <div
        style={{ display: currentStep >= STUDY_YEAR_STEP ? 'block' : 'none' }}
      >
        <AcademicCalendarComponent
          onStudyYearChange={setStudyYear}
          onValidChange={setIsCalendarValid}
          setupStep={
            currentStep === STUDY_YEAR_STEP ? 'studyYear' : 'semesters'
          }
        />
      </div>

      {currentStep >= STUDY_YEAR_STEP && !studyYear && (
        <Alert
          type="info"
          showIcon
          message={t('studyYearSetup.calendarAlreadyCreated')}
          style={{ marginTop: 16 }}
        />
      )}

      <Space style={{ marginTop: 24, width: '100%', justifyContent: 'end' }}>
        {currentStep > 0 && (
          <Button onClick={() => setCurrentStep(currentStep - 1)}>
            {t('studyYearSetup.previous')}
          </Button>
        )}
        {currentStep < CALENDAR_STEP ? (
          <Button type="primary" onClick={goToNextStep}>
            {t('studyYearSetup.next')}
          </Button>
        ) : (
          <Button
            type="primary"
            disabled={!studyYear || !isCalendarValid}
            loading={startStudyYearMutation.isPending}
            onClick={submit}
          >
            {t('studyYearSetup.submit')}
          </Button>
        )}
      </Space>
    </Form>
  );
};
